import { useState } from "react"
import { Link, useNavigate } from "react-router"
import type { DeliveryRow, NewsletterRow } from "@client/admin/types"
import { useRows } from "@client/store/entities"
import {
  ContentEmptyState,
  ContentNav,
  ContentPagination,
  ContentStatusBadge,
} from "./content-components"
import { useHasPermission } from "./session-context"

const PAGE_SIZE = 10

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

function NewsletterTableSkeleton() {
  return (
    <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
      <div className="space-y-0 divide-y divide-gold-500/30">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="grid animate-pulse grid-cols-4 gap-4 px-5 py-5">
            <div className="h-4 rounded bg-cream-100" />
            <div className="h-4 rounded bg-cream-100" />
            <div className="h-4 w-20 rounded bg-cream-100" />
            <div className="h-4 w-24 rounded bg-cream-100" />
          </div>
        ))}
      </div>
    </div>
  )
}

export function NewslettersPage() {
  const canMutate = useHasPermission("content_mutate")
  const navigate = useNavigate()
  const newsletterState = useRows("newsletter")
  const deliveryState = useRows("delivery")
  const loadedNewsletters = newsletterState.rows as NewsletterRow[] | null
  const newsletters = loadedNewsletters ?? []
  const deliveries = (deliveryState.rows ?? []) as DeliveryRow[]
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(1)

  const filtered = newsletters
    .filter((row) => {
      const query = search.trim().toLowerCase()
      return !query || row.title.toLowerCase().includes(query) || row.subject.toLowerCase().includes(query)
    })
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const sentCount = newsletters.filter((row) => row.status === "sent").length
  const draftCount = newsletters.filter((row) => row.status === "draft").length
  const deliveredCount = deliveries.filter((row) => row.status === "sent").length

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Newsletters</h1>
          <p className="mt-1 text-sm text-ink/60">Draft, send, and track updates from the foundation.</p>
        </div>
        {canMutate && (
          <Link
            to="/admin/newsletters/new"
            className="inline-flex h-10 items-center justify-center rounded-full bg-forest-950 px-5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
          >
            New newsletter
          </Link>
        )}
      </div>

      <ContentNav />

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ["Total campaigns", newsletters.length],
          ["Drafts", draftCount],
          ["Sent", sentCount],
        ].map(([label, value]) => (
          <div key={label} className="rounded-[10px] border border-gold-500 bg-white p-5">
            <p className="text-sm text-ink/50">{label}</p>
            <p className="mt-2 font-display text-3xl font-bold text-forest-950">
              {loadedNewsletters === null ? "..." : value}
            </p>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder="Search title or subject..."
            className="w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
          />
        </div>
        <p className="text-sm text-ink/50">{deliveredCount} delivered messages</p>
      </div>

      {newsletterState.error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-sm text-red-600">{newsletterState.error}</p>
          <button
            type="button"
            onClick={() => void newsletterState.retry()}
            className="mt-3 text-sm font-medium text-red-700 underline"
          >
            Try again
          </button>
        </div>
      ) : loadedNewsletters === null ? (
        <NewsletterTableSkeleton />
      ) : pageRows.length === 0 ? (
        <ContentEmptyState message={search ? "No newsletters match your search." : "No newsletters yet."} />
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-left">
              <thead className="border-b border-gold-500/50 bg-cream-50">
                <tr>
                  {[
                    "Title",
                    "Subject",
                    "Status",
                    "Delivery",
                    "Date",
                  ].map((heading) => (
                    <th key={heading} className="px-5 py-4 text-xs font-semibold uppercase tracking-wider text-ink/60">
                      {heading}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-500/30">
                {pageRows.map((row) => {
                  const rowDeliveries = deliveries.filter((delivery) => delivery.newsletterId === row.id)
                  const sent = rowDeliveries.filter((delivery) => delivery.status === "sent").length
                  const failed = rowDeliveries.filter((delivery) => delivery.status === "failed").length

                  return (
                    <tr
                      key={row.id}
                      onClick={() => navigate(`/admin/newsletters/${row.id}`)}
                      className="cursor-pointer transition-colors hover:bg-cream-50"
                    >
                      <td className="px-5 py-4">
                        <Link
                          to={`/admin/newsletters/${row.id}`}
                          onClick={(event) => event.stopPropagation()}
                          className="font-medium text-forest-900 hover:underline"
                        >
                          {row.title}
                        </Link>
                      </td>
                      <td className="max-w-[18rem] truncate px-5 py-4 text-sm text-ink/60">{row.subject}</td>
                      <td className="px-5 py-4"><ContentStatusBadge value={row.status} /></td>
                      <td className="px-5 py-4 text-sm text-ink/60">
                        {row.status === "sent" && rowDeliveries.length > 0 ? (
                          <span>
                            {sent}/{rowDeliveries.length}
                            {failed > 0 && <span className="ml-1 text-red-600">({failed} failed)</span>}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-ink/50">
                        {formatDate(row.status === "sent" && row.sentAt ? row.sentAt : row.createdAt)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onPageChange={setPage} disabled={loadedNewsletters === null} />
    </div>
  )
}
